import React, { useCallback } from "react"
import { Animated, DimensionValue } from "react-native"
import { View } from "react-native"

import { AppModuleBanner } from "@treefort/api-spec"

import Banner, { getBannerHeight } from "../banner"
import BannerItem from "../banner-item"
import TokensProvider, { useTokens } from "../tokens-provider"

export { getBannerHeight }

export function BannerModule({
  items,
  height,
  scrollRestorationKey,
  autoplay = false,
  paddingTop = 0,
}: {
  items: AppModuleBanner["config"]["items"]
  height: Exclude<DimensionValue, null | Animated.AnimatedNode>
  scrollRestorationKey?: string
  autoplay?: boolean
  paddingTop?: number
}): React.JSX.Element {
  const { tokens, displayWidth } = useTokens()
  const intervals = items?.length || 1
  const { backgroundColor, gradientOffset, gradientCoverage, scrollIndicator } =
    tokens.bannerModule

  // Make room for the scroll indicator when it's present
  const itemPaddingBottom =
    intervals > 1 ? scrollIndicator.container.height : tokens.spacing.large

  const bannerItemHeight =
    paddingTop && typeof height === "number" ? height - paddingTop : height

  const renderItems = useCallback(
    ({
      itemWidth,
      currentIndex,
    }: {
      itemWidth: number
      currentIndex: number
    }) =>
      items.map((item, index) => {
        // Only render the visible item + 2 on either side. This helps keep the
        // banner buttery smooth.
        return Math.abs(currentIndex - index) < 3 ? (
          <BannerItem
            height={bannerItemHeight}
            style={{ width: itemWidth, height: bannerItemHeight }}
            bannerItem={item}
            key={index}
            paddingBottom={itemPaddingBottom}
            backgroundColor={backgroundColor}
            gradientOffset={gradientOffset}
            gradientCoverage={gradientCoverage}
            touchableArea="view"
          />
        ) : (
          <View
            key={index}
            style={{ width: itemWidth, height: bannerItemHeight }}
          />
        )
      }) || [],
    [
      gradientCoverage,
      gradientOffset,
      itemPaddingBottom,
      bannerItemHeight,
      backgroundColor,
      items,
    ],
  )

  return (
    <TokensProvider displayMode="dark">
      <Banner
        paddingTop={paddingTop}
        renderItems={renderItems}
        autoplay={autoplay}
        height={height}
        width={displayWidth}
        scrollRestorationKey={scrollRestorationKey}
      />
    </TokensProvider>
  )
}
